import React, { useEffect, useState } from 'react'
import {
  Input,
  Form,
  Col,
  Button,
  message,
  Table,
  Row,
  Tooltip,
  Popconfirm,
} from 'antd'
import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import Layouts from '../../../components/Layouts'
import apiRequest from '../../../hooks/ApiRequest'

const ManageInsurerProducts = props => {
  const { form } = props
  const { getFieldDecorator, setFieldsValue, resetFields, validateFields } = form
  const [productList, setProductList] = useState([])
  const [productId, setProductId] = useState(0)
  const LoginInsurerId = JSON.parse(localStorage.getItem('insurerId'))
  const [editRecord, setEditRecord] = useState(null)

  // 🧱 Table Columns
  const columns = [
    {
      key: '1',
      title: 'Product Name',
      dataIndex: 'productName',
    },
    {
      key: '2',
      title: 'Reinsurance Limit',
      dataIndex: 'maximumLimit',
    },
    {
      key: '3',
      title: 'Minimum Premium',
      dataIndex: 'minimumPremium',
    },
    {
      key: '4',
      title: 'Sum Insured',
      dataIndex: 'sumInsured',
    },
    {
      key: '5',
      title: 'Action',
      render: (_, record) => (
        <>
          <Tooltip title="Edit">
            <Button
              type="primary"
              className="edit_btn"
              onClick={() => handleEditProduct(record)}>
              <EditOutlined />
            </Button>
          </Tooltip>
          <Popconfirm
            className="delete_btn"
            title="Are you sure you want to delete this record?"
            onConfirm={() => handleDeleteProduct(record.productId)}>
            <Tooltip title="Delete">
              <Button type="danger" style={{ marginLeft: 8 }}>
                <DeleteOutlined />
              </Button>
            </Tooltip>
          </Popconfirm>
        </>
      ),
    },
  ]

  // 🗑️ Delete Product
  const handleDeleteProduct = async productId => {
    try {
      const res = await apiRequest(
        `${process.env.REACT_APP_BASE_URL_INSURER}/api/Insurer/DeactivateInsurerProduct/${productId}`,
        'POST'
      )

      if (res.success) {
        message.success('Product Deleted Successfully !!').then(() => {
          fetchProducts()
        })
      } else {
        message.error('Failed to delete product.')
      }
    } catch (error) {
      console.error('Delete Products Error:', error)
      message.error('Failed to delete products!')
    }
  }

  // 🧩 Fetch all insurer products (now includes sumInsured)
  const fetchProducts = async () => {
    try {
      const response = await apiRequest(
        `${process.env.REACT_APP_BASE_URL_INSURER}/api/Insurer/GetInsurerProductByInsurerId/${LoginInsurerId}`,
        'GET'
      )

      if (response.success) {
        setProductList(response.data || [])
      } else {
        message.error('Failed to fetch products!')
      }
    } catch (error) {
      console.error('Fetch Products Error:', error)
      message.error('Failed to fetch products!')
    }
  }

  useEffect(() => {
    fetchProducts()
  }, [])

  // 🖋️ Pre-fill form when editing
  useEffect(() => {
    if (editRecord && editRecord.productId === productId) {
      setFieldsValue({
        maximumLimit: editRecord.maximumLimit,
        minimumPremium: editRecord.minimumPremium,
        sumInsured: editRecord.sumInsured,
      })
    }
  }, [productId, editRecord])

  const handleEditProduct = record => {
    setEditRecord(record)
    setProductId(record.productId)
  }

  // Update Product Handler
  const handleSubmit = async e => {
    e.preventDefault()
    try {
      const values = await validateFields()
      const { maximumLimit, minimumPremium, sumInsured } = values

      const payload = {
        productid: productId,
        minLimit: 0,
        maxLimit: parseFloat(maximumLimit),
        minimumPremium: parseFloat(minimumPremium),
        sumInsured: parseFloat(sumInsured),
      }

      const url = `${process.env.REACT_APP_BASE_URL_INSURER}/api/Insurer/UpdateProductLimits`

      const response = await apiRequest(url, 'POST', payload)

      if (response.success) {
        message.success('Product Updated Successfully!')
        resetFields()
        setProductId(0)
        fetchProducts()
      } else {
        message.error('Something went wrong!')
      }
    } catch (error) {
      console.error('Submit Product Error:', error)
      message.error('Failed to process request!')
    }
  }

  return (
    <Layouts title="Manage Products" classname="dashboard">
      <div className="dash-bg-white">
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <div className="section-top-heading">
              <h3 style={{ color: '#0C1362', fontWeight: '600', fontSize: '20px' }}>
                Manage Products
              </h3>
            </div>
          </Col>
        </Row>
      </div>

      <div>
        <Row className="m-t-30 m-b-40">
          {productId !== 0 && (
            <div className="dash-bg-white" style={{ padding: '20px', width: '100%' }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={10}>
                  <label>Update Product:</label>
                </Col>
                <Col xs={24} md={14}>
                  <Form layout="vertical" onSubmit={handleSubmit}>
                    <Row gutter={[16, 0]}>
                      <Col xs={24} md={8}>
                        <Form.Item label="Reinsurance Limit">
                          {getFieldDecorator('maximumLimit', {
                            rules: [{ required: true, message: 'Please enter product limit' }],
                          })(<Input type="number" min={0} placeholder="Enter Product Limit" />)}
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={8}>
                        <Form.Item label="Minimum Premium">
                          {getFieldDecorator('minimumPremium', {
                            rules: [
                              { required: true, message: 'Please enter minimum premium' },
                              {
                                validator: (_, value, cb) => {
                                  if (value === undefined || value === '') cb()
                                  else if (Number(value) < 0)
                                    cb('Minimum premium cannot be less than 0')
                                  else cb()
                                },
                              },
                            ],
                          })(<Input type="number" min={0} placeholder="Enter Minimum Premium" />)}
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={8}>
                        <Form.Item label="Sum Insured">
                          {getFieldDecorator('sumInsured', {
                            rules: [{ required: true, message: 'Please enter sum insured' }],
                          })(<Input type="number" min={0} placeholder="Enter Sum Insured" />)}
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={6}>
                        <Button htmlType="submit" className="primary m-t-22">
                          Update Product
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </Col>
              </Row>
            </div>
          )}

          <div className="table-grid-bx">
            <Table
              className="table-scroll-auto"
              columns={columns}
              dataSource={productList}
              pagination={false}
              rowKey="productId"
            />
          </div>
        </Row>
      </div>
    </Layouts>
  )
}

export default Form.create()(ManageInsurerProducts)
